import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Dropdown, Badge, Spinner, Modal } from 'react-bootstrap';
import WFBackofficeSidebar from '../../components/WFBackofficeSidebar';
import { useAuth } from '../../hooks/useAuth';
import { notificationApi, wfbackofficeApi, searchApi } from '../../services/api';
import SearchResults from '../../components/SearchResults';
import {
  FaUsers,
  FaFileAlt,
  FaCheckCircle,
  FaMoneyBillWave,
  FaChartLine
} from 'react-icons/fa';
import './Dashboard.css';

// Import icons
import { FaSignOutAlt, FaUser, FaBell, FaQuestionCircle, FaSearch, FaTimes } from 'react-icons/fa';

const WFBackofficeDashboard = () => {
  const navigate = useNavigate();
  const { logout, getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();

  // --- Notification State ---
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  // --- End Notification State ---
  
  // --- Search State ---
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchRef = useRef(null);
  // --- End Search State ---
  
  // --- Dashboard Stats State ---
  const [loading, setLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    totalLeads: 0,
    processedLeads: 0,
    totalAmount: 0,
    totalAgents: 0,
    recentInvoices: []
  });
  // --- End Dashboard Stats State ---
    
  // Set document title and check authentication
  useEffect(() => {
    document.title = 'Wishfin Backoffice Dashboard';
    
    // Check authentication
    const token = localStorage.getItem('token');
    const userJSON = localStorage.getItem('user');
    
    if (!token || !userJSON) {
      console.log('No token or user found, redirecting to login');
      navigate('/login');
      return;
    }
    
    try {
      const user = JSON.parse(userJSON);
      if (user.user_type !== 'WFBACKOFFICE') {
        console.log('User is not WFBACKOFFICE type, redirecting');
        navigate('/login');
        return;
      }
      
      console.log('User authenticated as WFBACKOFFICE, proceeding');
      fetchDashboardStats();
    } catch (error) {
      console.error('Error parsing user data:', error);
      navigate('/login');
    }
  }, [navigate]);
  
  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      // Try to fetch from the API
      const response = await wfbackofficeApi.getDashboardStats();
      if (response && response.data) {
        setDashboardStats(response.data);
      } else {
        console.error('Invalid response from dashboard stats API');
        // Fallback to mock data if response is invalid
        setDashboardStats({
          totalLeads: 1245,
          processedLeads: 874,
          totalAmount: 28750000,
          totalAgents: 86,
          recentInvoices: [
            {
              id: 1,
              invoice_no: "INV-20250425-001",
              dso_name: "ABC Finance",
              amount: 125000,
              status: { code: 2, name: "Disbursed" },
              created_at: "2025-04-25T10:30:00Z"
            },
            {
              id: 2,
              invoice_no: "INV-20250424-002",
              dso_name: "XYZ Finance",
              amount: 78500,
              status: { code: 1, name: "Processing" },
              created_at: "2025-04-24T15:45:00Z"
            },
            {
              id: 3,
              invoice_no: "INV-20250423-003",
              dso_name: "PQR Finance",
              amount: 224000,
              status: { code: 0, name: "Generated" },
              created_at: "2025-04-23T09:15:00Z"
            }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Fallback to mock data in case of any error
      setDashboardStats({
        totalLeads: 1245,
        processedLeads: 874,
        totalAmount: 28750000,
        totalAgents: 86,
        recentInvoices: [
          {
            id: 1,
            invoice_no: "INV-20250425-001",
            dso_name: "ABC Finance",
            amount: 125000,
            status: { code: 2, name: "Disbursed" },
            created_at: "2025-04-25T10:30:00Z"
          },
          {
            id: 2,
            invoice_no: "INV-20250424-002",
            dso_name: "XYZ Finance",
            amount: 78500,
            status: { code: 1, name: "Processing" },
            created_at: "2025-04-24T15:45:00Z"
          },
          {
            id: 3,
            invoice_no: "INV-20250423-003",
            dso_name: "PQR Finance",
            amount: 224000,
            status: { code: 0, name: "Generated" },
            created_at: "2025-04-23T09:15:00Z"
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Render status badge
  const renderStatusBadge = (status) => {
    const STATUS_MAP = {
      0: { variant: "info", name: "Generated" },
      1: { variant: "warning", name: "Processing" },
      2: { variant: "success", name: "Disbursed" },
      3: { variant: "danger", name: "Rejected" }
    };
    
    const badgeInfo = STATUS_MAP[status.code] || { variant: "secondary", name: status.name || "Unknown" };
    return <span className={`backoffice-status-badge ${badgeInfo.variant}`}>{badgeInfo.name}</span>;
  };
  
  // Handle search input changes with debouncing
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (query.trim().length >= 2) {
      const timeoutId = setTimeout(() => {
        performSearch(query);
      }, 300); // 300ms debounce
      
      setSearchTimeout(timeoutId);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
    }
  };
  
  // Perform the search
  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await searchApi.globalSearch(query, { limit_per_type: 5 });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Handle search result click
  const handleResultClick = () => {
    setShowSearchResults(false);
    setShowSearchModal(false);
    setSearchQuery('');
  };
  
  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchRef]);

  // --- Notification useEffect Hooks ---
  // Fetch unread count periodically
  useEffect(() => {
    let intervalId;
    const fetchNotificationsCount = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    fetchNotificationsCount(); // Initial fetch
    intervalId = setInterval(fetchNotificationsCount, 60000); // Fetch every 60 seconds

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, []);

  // Fetch full notifications when dropdown is opened
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          // Fetch latest 10 notifications
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications for dropdown:', error);
          setNotifications([]); // Clear notifications on error
        } finally {
          setLoadingNotifications(false);
        }
      }
    };

    fetchFullNotifications();
  }, [showNotifications]);
  // --- End Notification useEffect Hooks ---
      
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // --- Notification Helper Functions ---
  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    // Only proceed if the notification exists and is unread
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      // Update local state optimistically
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      // Decrement count optimistically ONLY if it was unread
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return; // No unread notifications

    try {
      await notificationApi.markAllAsRead();
      // Update local state optimistically
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0); // Reset count
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) {
      console.error("Error formatting timestamp:", e);
      return timestamp;
    }
  };
  // --- End Notification Helper Functions ---

  return (
    <div className="backoffice-dashboard-layout">
      <WFBackofficeSidebar />
      
      <div className="backoffice-content-wrapper">
        <header className="backoffice-header">
          <div className="backoffice-greeting">
            <span className="backoffice-greeting-text">Hi, {currentUser?.name || 'Wishfin'} <span className="backoffice-greeting-emoji">👋</span></span>
          </div>
          <div className="backoffice-header-search" ref={searchRef}>
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search invoices, DSOs..." 
                value={searchQuery}
                onChange={handleSearchChange}
                onClick={() => {
                  if (searchQuery.trim().length >= 2) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {isSearching && <Spinner animation="border" size="sm" className="search-spinner" />}
              {searchQuery && !isSearching && (
                <FaTimes 
                  className="search-clear-icon" 
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                    setSearchResults([]);
                  }} 
                />
              )}
            </div>
            
            {/* Desktop Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-container">
                <SearchResults results={searchResults} onResultClick={handleResultClick} />
              </div>
            )}
            
            {/* Mobile Search Button */}
            <div className="mobile-search-button" onClick={() => setShowSearchModal(true)}>
              <FaSearch />
            </div>
          </div>
          
          <div className="backoffice-header-actions">
            <div className="backoffice-header-icon">
              <FaQuestionCircle size={18} />
            </div>
            {/* Notification Dropdown */}
            <Dropdown
              className="notification-dropdown backoffice-notifications"
              show={showNotifications}
              onToggle={(isOpen) => setShowNotifications(isOpen)}
              align="end"
            >
              <Dropdown.Toggle variant="link" id="dropdown-notifications" className="p-0 border-0 backoffice-header-icon">
                <FaBell size={18} />
                {unreadCount > 0 && (
                  <Badge pill bg="danger" className="notification-badge">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </Dropdown.Toggle>

              <Dropdown.Menu style={{ minWidth: '320px', maxHeight: '450px', overflowY: 'auto', padding: '0' }}>
                <Dropdown.Header className="d-flex justify-content-between align-items-center sticky-top bg-light">
                  <span>Notifications</span>
                  {unreadCount > 0 && (
                    <button className="btn btn-link btn-sm p-0" onClick={handleMarkAllAsRead}>
                      Mark all as read
                    </button>
                  )}
                </Dropdown.Header>
                {loadingNotifications ? (
                  <div className="text-center p-4">
                    <Spinner animation="border" size="sm" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <Dropdown.Item
                      key={notification.id}
                      className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                      onClick={() => handleMarkAsRead(notification.id)}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="notification-content">
                          <div className="notification-title">{notification.title || notification.type}</div>
                          <div className="notification-message">{notification.message}</div>
                          <div className="notification-timestamp">{formatTimestamp(notification.created_at)}</div>
                        </div>
                        {!notification.is_read && <div className="unread-dot"></div>}
                      </div>
                    </Dropdown.Item>
                  ))
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-4">No new notifications</Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </Dropdown>
            <div className="backoffice-user-profile">
              <div className="backoffice-avatar">
                <FaUser />
              </div>
              <div className="backoffice-user-info">
                <span className="backoffice-user-name">
                  {currentUser?.name || 'Wishfin User'}
                </span>
                <button className="backoffice-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        
        <div className="backoffice-main-content">
          {/* Dashboard Content */}
          <div className="backoffice-dashboard-welcome">
            <h1>Welcome to Wishfin Backoffice</h1>
            <p>Here's an overview of DSO operations and invoice management</p>
          </div>

          {/* Stats Cards */}
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading dashboard data...</p>
            </div>
          ) : (
            <>
              <div className="backoffice-stats-grid">
                <div className="backoffice-stat-card">
                  <div className="backoffice-stat-icon" style={{ backgroundColor: '#6366f1' }}>
                    <FaFileAlt />
                  </div>
                  <div className="backoffice-stat-details">
                    <p className="backoffice-stat-title">Total Leads</p>
                    <h2 className="backoffice-stat-value">{dashboardStats.totalLeads.toLocaleString()}</h2>
                  </div>
                </div>

                <div className="backoffice-stat-card">
                  <div className="backoffice-stat-icon" style={{ backgroundColor: '#0ea5e9' }}>
                    <FaCheckCircle />
                  </div>
                  <div className="backoffice-stat-details">
                    <p className="backoffice-stat-title">Processed Leads</p>
                    <h2 className="backoffice-stat-value">{dashboardStats.processedLeads.toLocaleString()}</h2>
                  </div>
                </div>

                <div className="backoffice-stat-card">
                  <div className="backoffice-stat-icon" style={{ backgroundColor: '#10b981' }}>
                    <FaMoneyBillWave />
                  </div>
                  <div className="backoffice-stat-details">
                    <p className="backoffice-stat-title">Total Amount</p>
                    <h2 className="backoffice-stat-value">₹{(dashboardStats.totalAmount / 100000).toLocaleString('en-IN', { maximumFractionDigits: 2 })} L</h2>
                  </div>
                </div>

                <div className="backoffice-stat-card">
                  <div className="backoffice-stat-icon" style={{ backgroundColor: '#f59e0b' }}>
                    <FaUsers />
                  </div>
                  <div className="backoffice-stat-details">
                    <p className="backoffice-stat-title">Total Agents</p>
                    <h2 className="backoffice-stat-value">{dashboardStats.totalAgents.toLocaleString()}</h2>
                  </div>
                </div>
              </div>

              {/* Recent Invoices */}
              <div className="backoffice-dashboard-card">
                <div className="backoffice-card-header">
                  <h3>Recent Invoices</h3>
                </div>
                <div className="backoffice-card-body p-0">
                  <table className="backoffice-table">
                    <thead>
                      <tr>
                        <th>Invoice No.</th>
                        <th>DSO</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardStats.recentInvoices.map(invoice => (
                        <tr key={invoice.id}>
                          <td>{invoice.invoice_no}</td>
                          <td>{invoice.dso_name}</td>
                          <td>₹{invoice.amount.toLocaleString()}</td>
                          <td>{renderStatusBadge(invoice.status)}</td>
                          <td>{new Date(invoice.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-center p-3 border-top">
                  <a href="/wfbackoffice/invoices" className="btn btn-outline-primary btn-sm">
                    View All Invoices
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Mobile Search Modal */}
      <Modal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        centered
        className="search-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Search</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mobile-search-input-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search invoices, DSOs..."
              value={searchQuery}
              onChange={handleSearchChange}
              autoFocus
            />
            {isSearching && <Spinner animation="border" size="sm" className="search-spinner" />}
            {searchQuery && !isSearching && (
              <FaTimes
                className="search-clear-icon"
                onClick={() => {
                  setSearchQuery('');
                  setShowSearchResults(false);
                  setSearchResults([]);
                }}
              />
            )}
          </div>
          
          {searchResults.length > 0 && (
            <div className="mobile-search-results">
              <SearchResults results={searchResults} onResultClick={handleResultClick} />
            </div>
          )}
          
          {searchQuery.trim().length >= 2 && searchResults.length === 0 && !isSearching && (
            <div className="no-results-message">
              No results found for "{searchQuery}"
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default WFBackofficeDashboard;